import { supabase } from './supabaseClient';
import { fileUploadService } from './fileUploadService';
import { AIPreReleaseProjectService } from './aiPreReleaseProjectService';
import { BusinessIdea } from '../types/ai';
import { JourneyMarket, JourneyPersona } from '../types/journey';

export interface PreReleaseProject {
  id: string;
  publisher_id: string;
  title: string;
  description: string;
  concept_details?: string;
  key_features: Array<{ text: string; screenshot_url?: string }> | string[];
  feature_screenshots?: {
    url: string;
    caption?: string;
  }[];
  estimated_timeline?: string;
  proposed_price?: number;
  early_bird_discount?: number;
  project_category?: string;
  project_type: 'app' | 'website' | 'product' | 'service' | 'other';
  status: 'concept' | 'development' | 'released' | 'cancelled';
  image_url?: string;
  demo_url?: string;
  created_at: string;
  updated_at: string;
  release_date?: string;
  is_active: boolean;
  publisher?: {
    username: string;
    avatar_url?: string;
  };
  // Additional fields from dashboard queries
  interest_count?: number;
  willing_to_pay_count?: number;
}

export interface ProjectInterest {
  id: string;
  project_id: string;
  user_id: string;
  interest_level: number;
  willing_to_pay: boolean;
  committed_to_buy: boolean;
  early_bird_commitment?: boolean;
  max_price_willing?: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectFeedback {
  id: string;
  project_id: string;
  user_id: string;
  feedback_type: 'feature_request' | 'improvement' | 'concern' | 'general';
  title?: string;
  description: string;
  priority_vote: number;
  created_at: string;
  updated_at: string;
  user?: {
    username: string;
    avatar_url?: string;
  };
}

export interface UserReliabilityMetrics {
  id: string;
  user_id: string;
  total_interests_expressed: number;
  actual_purchases_made: number;
  reliability_score: number;
  last_calculated: string;
}

class PreReleaseService {
  // Get all active pre-release projects (including released projects)
  async getActiveProjects(): Promise<PreReleaseProject[]> {
    try {
      const { data, error } = await supabase
        .from('pre_release_projects')
        .select(`
          *,
          publisher:publisher_id (
            username,
            avatar_url
          )
        `)
        .eq('is_active', true)
        .in('status', ['concept', 'development', 'released'])
        .order('created_at', { ascending: false });

      if (error) {
        // If table doesn't exist, return empty array for now
        if (error.code === '42P01') {
          console.warn('Pre-release tables not found. Please run database migrations.');
          return [];
        }
        throw error;
      }
      return data || [];
    } catch (error) {
      console.error('Error fetching active projects:', error);
      return [];
    }
  }

  // Get project by ID
  async getProjectById(id: string): Promise<PreReleaseProject | null> {
    try {
      const { data, error } = await supabase
        .from('pre_release_projects')
        .select(`
          *,
          publisher:publisher_id (
            username,
            avatar_url
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === '42P01') {
          return null;
        }
        throw error;
      }
      return data;
    } catch (error) {
      return null;
    }
  }

  // Upload screenshot to Supabase storage
  async uploadScreenshot(file: File, projectId: string, featureIndex: number): Promise<string> {
    try {
      const result = await fileUploadService.uploadProjectImage(file, projectId, featureIndex);
      return result.url;
    } catch (error) {
      console.error('Screenshot upload failed:', error);

      // Fallback to the original upload method for backward compatibility
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User must be authenticated to upload files');
      }

      // Validate file before uploading
      if (!file || file.size === 0) {
        throw new Error('Invalid file provided');
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error(`File type ${file.type} is not allowed. Please use JPEG, PNG, WebP, or GIF.`);
      }

      // Check file size (limit to 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File size must be less than 5MB');
      }

      // Try to ensure bucket exists
      try {
        await this.createProjectScreenshotsBucket();
      } catch (bucketError) {
        console.warn('Could not create bucket:', bucketError);
      }

      const fileExt = file.name.split('.').pop();
      const timestamp = Date.now();
      const fileName = `${user.id}/${projectId}/feature_${featureIndex}_${timestamp}.${fileExt}`;

      console.log('Attempting fallback upload for file:', fileName);

      // Upload to project-screenshots bucket
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('project-screenshots')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });

      if (uploadError) {
        console.error('Fallback upload error:', uploadError);

        // If it's an RLS policy error, try with a different approach
        if (uploadError.message?.includes('row-level security') ||
          uploadError.message?.includes('Unauthorized') ||
          uploadError.message?.includes('new row violates')) {

          // Try uploading to a public folder structure
          const publicFileName = `public/${user.id}/${projectId}/feature_${featureIndex}_${timestamp}.${fileExt}`;

          const { data: publicUploadData, error: publicUploadError } = await supabase.storage
            .from('project-screenshots')
            .upload(publicFileName, file, {
              cacheControl: '3600',
              upsert: false,
              contentType: file.type
            });

          if (publicUploadError) {
            console.error('Public upload also failed:', publicUploadError);
            throw new Error(`Failed to upload file: ${uploadError.message}. Please contact support if this issue persists.`);
          }

          // Get public URL for the successful public upload
          const { data: publicUrlData } = supabase.storage
            .from('project-screenshots')
            .getPublicUrl(publicFileName);

          return publicUrlData.publicUrl;
        }

        throw new Error(`Failed to upload file: ${uploadError.message}`);
      }

      // Get public URL from the successful upload
      const { data: urlData } = supabase.storage
        .from('project-screenshots')
        .getPublicUrl(fileName);

      console.log('Fallback upload successful, returning URL:', urlData.publicUrl);
      return urlData.publicUrl;
    }
  }

  // Test storage functionality
  async testStorageUpload(): Promise<boolean> {
    try {
      // Check if we can access the storage at all
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return false;
      }

      // Try to list files in the project-screenshots bucket first
      let { data: files, error: listError } = await supabase.storage
        .from('project-screenshots')
        .list('', { limit: 1 });

      if (listError && listError.message?.includes('Bucket not found')) {
        // Try the screenshots bucket as fallback
        const fallbackResult = await supabase.storage
          .from('screenshots')
          .list('', { limit: 1 });

        if (fallbackResult.error) {
          return false;
        }

        return true;
      }

      if (listError) {
        return false;
      }

      return true;

    } catch (error) {
      return false;
    }
  }

  // Create storage bucket for project screenshots
  async createProjectScreenshotsBucket(): Promise<void> {
    try {
      // First check if bucket already exists
      const { data: buckets, error: listError } = await supabase.storage.listBuckets();

      if (listError) {
        console.warn('Could not list buckets:', listError);
        return;
      }

      const projectScreenshotsBucketExists = buckets?.some(bucket => bucket.name === 'project-screenshots');

      if (projectScreenshotsBucketExists) {
        console.log('project-screenshots bucket already exists');
        return;
      }

      // Try to create bucket with proper configuration
      console.log('Creating project-screenshots bucket...');
      const { data: bucketData, error: createError } = await supabase.storage.createBucket('project-screenshots', {
        public: true,
        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
        fileSizeLimit: 5 * 1024 * 1024 // 5MB limit
      });

      if (createError) {
        console.warn('Could not create project-screenshots bucket:', createError);
        // Don't throw error, just warn - the app should still work if bucket exists
        return;
      }

      console.log('project-screenshots bucket created successfully');
    } catch (error) {
      console.warn('Error in createProjectScreenshotsBucket:', error);
      // Don't throw - this is a non-critical operation
    }
  }

  // Create AI-enhanced pre-release project based on business idea
  async createAIEnhancedProject(
    selectedIdea: BusinessIdea,
    market: JourneyMarket,
    persona: JourneyPersona,
    featureScreenshots?: (File | null)[]
  ): Promise<PreReleaseProject> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to create projects');

    try {
      // Generate AI-powered pre-release project
      const aiProject = await AIPreReleaseProjectService.generatePreReleaseProject(
        selectedIdea,
        market,
        persona
      );

      // Create the project with AI-generated content
      return await this.createProject({
        ...aiProject,
        publisher_id: user.id
      } as Omit<PreReleaseProject, 'id' | 'created_at' | 'updated_at' | 'publisher' | 'publisher_id'>, featureScreenshots, true);

    } catch (error) {
      console.error('AI-enhanced project creation failed:', error);
      // Fallback to regular project creation with basic content
      const fallbackProject: Omit<PreReleaseProject, 'id' | 'created_at' | 'updated_at' | 'publisher' | 'publisher_id'> = {
        title: `${selectedIdea.title} - Pre-Release`,
        description: `Test and validate ${selectedIdea.title} with real users before full development.`,
        concept_details: `This pre-release project validates the core assumptions of ${selectedIdea.title}: ${selectedIdea.problem}`,
        key_features: [
          { text: 'Core value proposition validation' },
          { text: 'Target customer feedback collection' },
          { text: 'Pricing and demand testing' },
          { text: 'User experience validation' },
          { text: 'Market fit assessment' }
        ],
        estimated_timeline: '2-3 weeks',
        proposed_price: 29,
        early_bird_discount: 50,
        project_category: 'Business Tools',
        project_type: 'app',
        status: 'concept',
        is_active: true
      };

      return await this.createProject(fallbackProject, featureScreenshots, true);
    }
  }

  // Generate beta program assets for a project
  async generateBetaProgramAssets(
    projectId: string,
    selectedIdea: BusinessIdea
  ): Promise<{
    landingPageCopy: string;
    emailTemplates: { welcome: string; feedback: string; launch: string };
    socialMediaPosts: string[];
    surveyQuestions: string[];
  }> {
    const project = await this.getProjectById(projectId);
    if (!project) {
      throw new Error('Project not found');
    }

    try {
      return await AIPreReleaseProjectService.generateBetaProgramAssets(project, selectedIdea);
    } catch (error) {
      console.error('Failed to generate beta program assets:', error);
      // Return basic fallback assets
      return {
        landingPageCopy: `# ${project.title}\n\n${project.description}\n\n**Join our beta program today!**`,
        emailTemplates: {
          welcome: `Welcome to the ${project.title} beta program!`,
          feedback: `How has your experience been with ${project.title}?`,
          launch: `${project.title} is now live! Thank you for being part of our beta.`
        },
        socialMediaPosts: [
          `Excited to announce our beta program for ${project.title}! 🚀`,
          `Looking for beta testers for ${project.title}. Join us!`,
          `Help us shape the future of ${project.title}. Beta access available now.`
        ],
        surveyQuestions: [
          'How often do you experience this problem?',
          'How well does our solution address your needs?',
          'What would you be willing to pay for this solution?',
          'What features are most important to you?',
          'How likely are you to recommend this to others?'
        ]
      };
    }
  }

  // Create new pre-release project with screenshot support
  async createProject(
    project: Omit<PreReleaseProject, 'id' | 'created_at' | 'updated_at' | 'publisher' | 'publisher_id'>,
    featureScreenshots?: (File | null)[],
    isAIGenerated: boolean = false
  ): Promise<PreReleaseProject> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to create projects');

    // First create the project to get an ID
    const projectWithPublisher = {
      ...project,
      publisher_id: user.id
    };

    const { data: newProject, error } = await supabase
      .from('pre_release_projects')
      .insert([projectWithPublisher])
      .select()
      .single();

    if (error) throw error;

    // Deduct points for pre-release project creation
    const creationType = isAIGenerated ? 'AI-generated' : 'manual';
    console.log(`💰 Pre-release project created (${creationType}), deducting points...`);
    try {
      const { AIPointsService } = await import('./aiPointsService');
      const requiredPoints = AIPointsService.getPointsCost('preReleaseProject');
      console.log('💰 Required points for pre-release:', requiredPoints);

      console.log(`💰 Deducting points for pre-release project (${creationType})...`);
      const deductResult = await AIPointsService.deductPoints(
        user.id,
        'preReleaseProject',
        newProject.id,
        undefined,
        isAIGenerated
      );

      console.log('💰 Points deduction result:', deductResult);

      if (!deductResult.success) {
        console.error('❌ Failed to deduct points for pre-release project:', deductResult.error);
        // Don't fail the project creation, just log the error
      } else {
        console.log(`✅ Successfully deducted ${requiredPoints} points for pre-release project creation`);
      }
    } catch (pointsError) {
      console.error('❌ Error deducting points:', pointsError);
      // Don't fail the project creation
    }

    // Upload screenshots and combine with features
    let enhancedKeyFeatures: Array<{ text: string; screenshot_url?: string }> = [];

    // First, create enhanced key features array with text
    project.key_features.forEach((feature, index) => {
      enhancedKeyFeatures.push({
        text: typeof feature === 'string' ? feature : feature.text,
        screenshot_url: undefined // Will be filled if screenshot upload succeeds
      });
    });

    if (featureScreenshots && featureScreenshots.length > 0) {
      for (let i = 0; i < featureScreenshots.length; i++) {
        const file = featureScreenshots[i];
        console.log(`🔍 Processing file ${i}:`, {
          hasFile: !!file,
          isFile: file instanceof File,
          fileName: file?.name,
          fileSize: file?.size
        });

        if (file && file instanceof File && i < enhancedKeyFeatures.length) {
          try {
            console.log(`📤 Uploading screenshot ${i} for feature: "${project.key_features[i]}"`);
            console.log(`📄 File details:`, {
              name: file.name,
              size: file.size,
              type: file.type
            });

            const url = await this.uploadScreenshot(file, newProject.id, i);

            if (url && url.trim()) {
              enhancedKeyFeatures[i].screenshot_url = url;
              console.log(`📊 Enhanced feature ${i}:`, enhancedKeyFeatures[i]);
            } else {
            }
          } catch (uploadError) {
            // Continue with other screenshots even if one fails
            // But log the specific error for debugging
            console.error('Upload error details:', {
              featureIndex: i,
              featureName: project.key_features[i],
              fileName: file.name,
              error: uploadError
            });

            // If it's a permissions error, we'll continue without screenshots
            // rather than failing the entire project creation
            if (uploadError instanceof Error && (
              uploadError.message?.includes('Storage permissions not configured') ||
              uploadError.message?.includes('Unauthorized') ||
              uploadError.message?.includes('row-level security'))) {
            }
          }
        } else {
        }
      }
    }
    // Check if any features have screenshot URLs
    const hasScreenshots = enhancedKeyFeatures.some(feature => feature.screenshot_url);

    const updateData = {
      key_features: enhancedKeyFeatures,
      updated_at: new Date().toISOString()
    };


    const { data: updatedProject, error: updateError } = await supabase
      .from('pre_release_projects')
      .update(updateData)
      .eq('id', newProject.id)
      .select()
      .single();

    if (updateError) {
      return newProject;
    }

    // Verify the enhanced features were saved correctly
    if (updatedProject.key_features && Array.isArray(updatedProject.key_features)) {
      updatedProject.key_features.forEach((feature: any, index: number) => {
        console.log(`Feature ${index}:`, feature);
      });
    } else {
    }

    return updatedProject;
  }

  // Update project
  async updateProject(id: string, updates: Partial<PreReleaseProject>): Promise<PreReleaseProject> {
    const { data, error } = await supabase
      .from('pre_release_projects')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Express interest in a project
  async expressInterest(projectId: string, interest: Omit<ProjectInterest, 'id' | 'created_at' | 'updated_at' | 'project_id' | 'user_id'>): Promise<ProjectInterest> {
    try {
      // First, try to get the current user from auth
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Check if user already has interest in this project
      const { data: existingInterest } = await supabase
        .from('project_interest')
        .select('*')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .single();

      let data, error;

      if (existingInterest) {
        // Update existing interest
        ({ data, error } = await supabase
          .from('project_interest')
          .update({
            ...interest,
            updated_at: new Date().toISOString()
          })
          .eq('project_id', projectId)
          .eq('user_id', user.id)
          .select()
          .single());
      } else {
        // Insert new interest
        ({ data, error } = await supabase
          .from('project_interest')
          .insert([{
            project_id: projectId,
            user_id: user.id,
            ...interest
          }])
          .select()
          .single());
      }

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error expressing interest:', error);
      throw error;
    }
  }

  // Get user's interest in a project
  async getUserInterest(projectId: string): Promise<ProjectInterest | null> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return null;
    }

    const { data, error } = await supabase
      .from('project_interest')
      .select('*')
      .eq('project_id', projectId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  // Get project interest summary
  async getProjectInterestSummary(projectId: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .rpc('get_project_interest_summary', { project_uuid: projectId });

      if (error) {
        if (error.code === '42883' || error.code === '42P01') {
          console.warn('Pre-release functions not found. Please run database migrations.');
          return { total_interested: 0, willing_to_pay: 0, average_interest_level: 0 };
        }
        throw error;
      }
      return data || { total_interested: 0, willing_to_pay: 0, average_interest_level: 0 };
    } catch (error) {
      console.error('Error fetching project interest summary:', error);
      return { total_interested: 0, willing_to_pay: 0, average_interest_level: 0 };
    }
  }

  // Add feedback to a project
  async addFeedback(feedback: Omit<ProjectFeedback, 'id' | 'created_at' | 'updated_at' | 'user' | 'user_id'>): Promise<ProjectFeedback> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to add feedback');

    const feedbackWithUser = {
      ...feedback,
      user_id: user.id
    };

    const { data, error } = await supabase
      .from('project_feedback')
      .insert([feedbackWithUser])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Get project feedback
  async getProjectFeedback(projectId: string): Promise<ProjectFeedback[]> {
    try {
      const { data, error } = await supabase
        .from('project_feedback')
        .select(`
          *,
          user:user_id (
            username,
            avatar_url
          )
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === '42P01') {
          console.warn('Pre-release tables not found. Please run database migrations.');
          return [];
        }
        throw error;
      }
      return data || [];
    } catch (error) {
      console.error('Error fetching project feedback:', error);
      return [];
    }
  }

  // Get user reliability metrics
  async getUserReliability(userId: string): Promise<UserReliabilityMetrics | null> {
    const { data, error } = await supabase
      .from('user_reliability_metrics')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  // Get publisher dashboard data
  async getPublisherDashboard(userId?: string): Promise<any> {
    try {
      // Use provided userId or fall back to getting it from auth
      const publisherId = userId || (await supabase.auth.getUser()).data.user?.id;

      if (!publisherId) {
        console.warn('No user ID available for publisher dashboard');
        return { total_projects: 0, active_projects: 0, released_projects: 0, total_interest: 0, total_willing_to_pay: 0, projects: [] };
      }

      const { data, error } = await supabase
        .rpc('get_publisher_dashboard', {
          publisher_uuid: publisherId
        });

      if (error) {
        if (error.code === '42883' || error.code === '42P01') {
          console.warn('Pre-release functions not found. Please run database migrations.');
          return { total_projects: 0, active_projects: 0, released_projects: 0, total_interest: 0, total_willing_to_pay: 0, projects: [] };
        }
        throw error;
      }
      return data || { total_projects: 0, active_projects: 0, released_projects: 0, total_interest: 0, total_willing_to_pay: 0, projects: [] };
    } catch (error) {
      console.error('Error fetching publisher dashboard:', error);
      return { total_projects: 0, active_projects: 0, released_projects: 0, total_interest: 0, total_willing_to_pay: 0, projects: [] };
    }
  }

  // Mark project as released and notify users
  async releaseProject(projectId: string): Promise<number> {
    // Update project status
    await this.updateProject(projectId, {
      status: 'released',
      release_date: new Date().toISOString()
    });

    // Try to notify interested users using the RPC function if it exists
    try {
      const { data, error } = await supabase
        .rpc('notify_interested_users_on_release', { project_uuid: projectId });

      if (error) {
        // If the RPC function doesn't exist, that's okay - we'll handle notifications through the UI
        if (error.code === '42883') {
          console.warn('Legacy notification function not found. Using new notification system.');
          return 0;
        }
        throw error;
      }
      return data || 0;
    } catch (error) {
      console.warn('Legacy notification system not available. Using new notification system.');
      return 0;
    }
  }

  // Get interested users for notification purposes
  async getInterestedUsersForNotification(projectId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('project_interest')
        .select(`
          *,
          user:user_id (
            id,
            email,
            username,
            avatar_url
          )
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === '42P01') {
          console.warn('Pre-release tables not found. Please run database migrations.');
          return [];
        }
        throw error;
      }

      // Transform the data to match the expected format
      return (data || []).map(interest => ({
        id: interest.id,
        user_id: interest.user_id,
        email: interest.user?.email || '',
        username: interest.user?.username || 'Unknown User',
        interest_level: interest.interest_level,
        willing_to_pay: interest.willing_to_pay,
        committed_to_buy: interest.committed_to_buy,
        early_bird_commitment: interest.early_bird_commitment,
        max_price_willing: interest.max_price_willing,
        created_at: interest.created_at
      }));
    } catch (error) {
      console.error('Error fetching interested users for notification:', error);
      return [];
    }
  }

  // Check if project can be released
  async canReleaseProject(projectId: string): Promise<{ canRelease: boolean; reason?: string }> {
    try {
      const project = await this.getProjectById(projectId);

      if (!project) {
        return { canRelease: false, reason: 'Project not found' };
      }

      if (project.status === 'released') {
        return { canRelease: false, reason: 'Project is already released' };
      }

      if (project.status === 'cancelled') {
        return { canRelease: false, reason: 'Cannot release a cancelled project' };
      }

      // Project can be released if it's in concept or development status
      return { canRelease: true };
    } catch (error) {
      console.error('Error checking if project can be released:', error);
      return { canRelease: false, reason: 'Error checking project status' };
    }
  }

  // Set notification preferences
  async setNotificationPreferences(projectId: string, emailNotifications: boolean, platformNotifications: boolean): Promise<void> {
    const { error } = await supabase
      .from('project_notifications')
      .upsert([{
        project_id: projectId,
        email_notifications: emailNotifications,
        platform_notifications: platformNotifications
      }]);

    if (error) throw error;
  }
}

export const preReleaseService = new PreReleaseService();